// =====================================================================
// CarrierFormModal.jsx - Create/edit TripCarrier (M-002 Day 3)
// =====================================================================
// Pattern identic TypeFormModal (M-005).
// Permission: trips.* (reuz din CRUD trips, NOT trip_carriers.*).
// =====================================================================

import { useState, useEffect } from 'react';
import { tripCarriers as carriersApi } from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};
const modalStyle = {
  background: '#fff', borderRadius: 8, padding: '1.5rem 1.75rem',
  width: '90%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto',
  boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
};
const labelStyle = {
  display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#374151',
  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.375rem',
};
const inputStyle = {
  width: '100%', padding: '0.5rem 0.625rem', border: '1px solid #d1d5db',
  borderRadius: 4, fontSize: '0.875rem', background: '#fff', boxSizing: 'border-box',
};
const btnPrimary = { background: '#2563eb', color: '#fff', border: 0, padding: '0.5rem 1.25rem', borderRadius: 4, fontWeight: 500, fontSize: '0.875rem', cursor: 'pointer' };
const btnSecondary = { background: 'transparent', color: '#6b7280', border: '1px solid #d1d5db', padding: '0.5rem 1.25rem', borderRadius: 4, fontWeight: 500, fontSize: '0.875rem', cursor: 'pointer' };
const btnDanger = { background: 'transparent', color: '#b91c1c', border: '1px solid #fca5a5', padding: '0.5rem 1.25rem', borderRadius: 4, fontWeight: 500, fontSize: '0.875rem', cursor: 'pointer' };

export default function CarrierFormModal({ carrierId, onClose, onSaved }) {
  const { can } = useAuth();
  const isEdit = !!carrierId;

  // Permissions - tripCarriers reuse trips.* perms
  const canCreate = can('trips.create.all');
  const canUpdate = can('trips.update.all');
  const canDelete = can('trips.delete.all');

  const [code, setCode]         = useState('');
  const [label, setLabel]       = useState('');
  const [notes, setNotes]       = useState('');
  const [isActive, setIsActive] = useState(true);
  const [tripsCount, setTripsCount] = useState(0);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!isEdit) { setLoading(false); return; }
    async function load() {
      try {
        const c = await carriersApi.get(carrierId);
        if (cancelled) return;
        setCode(c.code);
        setLabel(c.label);
        setNotes(c.notes || '');
        setIsActive(c.isActive);
        setTripsCount(c._count?.trips ?? 0);
      } catch (err) {
        console.error('[CarrierFormModal] load:', err);
        setError(err.response?.data?.message || 'Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [carrierId, isEdit]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleSubmit(e) {
    e?.preventDefault();
    setError(null);

    if (!code.trim()) return setError('Code requis');
    if (!label.trim()) return setError('Libellé requis');

    setSaving(true);
    try {
      const data = {
        code: code.trim().toUpperCase(),
        label: label.trim(),
        notes: notes.trim() || null,
        isActive,
      };
      if (isEdit) await carriersApi.update(carrierId, data);
      else        await carriersApi.create(data);
      onSaved();
    } catch (err) {
      console.error('[CarrierFormModal] save:', err);
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Supprimer le transporteur ${code} ?\n\nNote: si le transporteur a des trajets associés, il sera désactivé.`)) return;
    setSaving(true);
    try {
      await carriersApi.remove(carrierId);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div style={overlayStyle} onClick={onClose}>
        <div style={modalStyle} onClick={e => e.stopPropagation()}>
          <p style={{ textAlign: 'center', color: '#6b7280' }}>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 1.25rem', fontSize: '1.25rem', fontWeight: 600 }}>
          {isEdit ? 'Modifier transporteur' : 'Nouveau transporteur'}
        </h2>

        {error && (
          <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.625rem 0.875rem', borderRadius: 4, marginBottom: '1rem', fontSize: '0.8125rem' }}>{error}</div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Code * (ex: BRAIN, GLS, DHL)</label>
            <input
              type="text"
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              style={{ ...inputStyle, fontFamily: 'monospace', textTransform: 'uppercase' }}
              maxLength={20}
              disabled={saving || isEdit}
              required
              placeholder="BRAIN"
            />
            {isEdit && <small style={{ color: '#9ca3af', fontSize: '0.6875rem' }}>Le code ne peut pas être changé.</small>}
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Libellé *</label>
            <input
              type="text"
              value={label}
              onChange={e => setLabel(e.target.value)}
              style={inputStyle}
              maxLength={100}
              disabled={saving}
              required
              placeholder="Ex: Brain E-Log (transporteur interne)"
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Notes (optionnel)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              style={{ ...inputStyle, minHeight: 60, fontFamily: 'inherit' }}
              maxLength={500}
              disabled={saving}
            />
          </div>

          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#374151', cursor: 'pointer' }}>
              <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} disabled={saving} />
              Transporteur actif
            </label>
          </div>

          {isEdit && tripsCount > 0 && (
            <div style={{ background: '#fef3c7', color: '#92400e', padding: '0.5rem 0.75rem', borderRadius: 4, fontSize: '0.75rem', marginBottom: '1rem' }}>
              ⚠ {tripsCount} trajet(s) lié(s). Suppression = désactivation seulement.
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              {isEdit && canDelete && (
                <button type="button" style={btnDanger} onClick={handleDelete} disabled={saving}>Supprimer</button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" style={btnSecondary} onClick={onClose} disabled={saving}>Annuler</button>
              {(canCreate || (isEdit && canUpdate)) && (
                <button type="submit" style={btnPrimary} disabled={saving}>
                  {saving ? 'Enregistrement...' : (isEdit ? 'Enregistrer' : 'Créer')}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
